import { useCallback, useEffect, useRef } from 'react';
import { Box, IconButton, Tooltip } from '@material-ui/core';
import ZoomInIcon from '@material-ui/icons/ZoomIn';
import ZoomOutIcon from '@material-ui/icons/ZoomOut';
import ZoomOutMapIcon from '@material-ui/icons/ZoomOutMap';
import { select } from 'd3-selection';
import { zoom as d3zoom, zoomIdentity, type ZoomBehavior } from 'd3-zoom';

const SCALE_STEP = 1.3;

/**
 * Takes over pan/zoom from DependencyGraph, which binds its own behaviour with
 * `scaleExtent([1, Infinity])` and so can never zoom out past the fitted scale.
 * Pass `zoom="disabled"` to the graph so the two do not fight over the events.
 */
export function useGraphZoom() {
  const containerRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement | null>(null);
  const behaviorRef = useRef<ZoomBehavior<SVGSVGElement, unknown> | null>(null);

  useEffect(() => {
    const host = containerRef.current;
    if (!host) return undefined;

    // The graph mounts asynchronously after layout settles.
    const bind = () => {
      const svg = host.querySelector<SVGSVGElement>('svg#dependency-graph');
      const workspace = svg?.querySelector<SVGGElement>('#workspace');
      if (!svg || !workspace || svgRef.current === svg) return;

      const behavior = d3zoom<SVGSVGElement, unknown>()
        .scaleExtent([0.2, 4])
        // d3's default extent reads svg.width.baseVal, which the graph never sets.
        .extent((): [[number, number], [number, number]] => {
          const { width, height } = host.getBoundingClientRect();
          return [
            [0, 0],
            [width || 1, height || 1],
          ];
        })
        .on('zoom', event => {
          select(workspace).attr('transform', event.transform.toString());
        });

      svgRef.current = svg;
      behaviorRef.current = behavior;
      select(svg).call(behavior).style('cursor', 'grab');
    };

    bind();
    const observer = new MutationObserver(bind);
    observer.observe(host, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);

  const scaleBy = useCallback((factor: number) => {
    const svg = svgRef.current;
    const behavior = behaviorRef.current;
    if (svg && behavior) select(svg).call(behavior.scaleBy, factor);
  }, []);

  const zoomIn = useCallback(() => scaleBy(SCALE_STEP), [scaleBy]);
  const zoomOut = useCallback(() => scaleBy(1 / SCALE_STEP), [scaleBy]);
  const resetZoom = useCallback(() => {
    const svg = svgRef.current;
    const behavior = behaviorRef.current;
    if (svg && behavior) select(svg).call(behavior.transform, zoomIdentity);
  }, []);

  return { containerRef, zoomIn, zoomOut, resetZoom };
}

export const GraphZoomControls = ({
  zoomIn,
  zoomOut,
  resetZoom,
}: {
  zoomIn: () => void;
  zoomOut: () => void;
  resetZoom: () => void;
}) => (
  <Box display="flex" alignItems="center">
    <Tooltip title="Zoom in">
      <IconButton size="small" aria-label="Zoom in" onClick={zoomIn} style={{ color: '#8b949e' }}>
        <ZoomInIcon fontSize="small" />
      </IconButton>
    </Tooltip>
    <Tooltip title="Zoom out">
      <IconButton size="small" aria-label="Zoom out" onClick={zoomOut} style={{ color: '#8b949e' }}>
        <ZoomOutIcon fontSize="small" />
      </IconButton>
    </Tooltip>
    <Tooltip title="Reset zoom">
      <IconButton size="small" aria-label="Reset zoom" onClick={resetZoom} style={{ color: '#8b949e' }}>
        <ZoomOutMapIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  </Box>
);
